import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Modal, message, Typography, Space, Alert, Radio } from 'antd';
import { SendOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { sendBatch, listUploadBatches, listUploadBatchesByIds } from '../../actions/uploadBatchActions';
import { sendCohort, listCohorts, listCohortsByIds } from '../../actions/cohortActions';
import { UPLOAD_BATCH_SEND_BATCH_RESET } from '../../constants/uploadBatchConstants';
import { COHORT_SEND_COHORT_RESET } from '../../constants/cohortConstants';
import SingleEntitySelectMiniature from '../crud/SingleEntitySelectMiniature.component';

const { Text, Paragraph } = Typography;

const SendBatchButton = ({ initialUploadBatchId, ...buttonProps }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [sendType, setSendType] = useState('batch'); // 'batch' or 'cohort'
  const [selectedUploadBatchId, setSelectedUploadBatchId] = useState(initialUploadBatchId || null);
  const [selectedCohortId, setSelectedCohortId] = useState(null);
  
  const { loading: batchLoading, success: batchSuccess, error: batchError, updatedCount: batchUpdatedCount } = useSelector(
    (state) => state.uploadBatchSendBatch
  );
  
  const { loading: cohortLoading, success: cohortSuccess, error: cohortError, updatedCount: cohortUpdatedCount } = useSelector(
    (state) => state.cohortSendCohort
  );
  
  const loading = batchLoading || cohortLoading;
  const success = batchSuccess || cohortSuccess;
  const error = batchError || cohortError;
  const updatedCount = batchUpdatedCount || cohortUpdatedCount;

  useEffect(() => {
    if (success) {
      const entityType = sendType === 'batch' ? t('Batch') : t('Cohort');
      message.success(
        t('{{entityType}} sent successfully. {{count}} subject(s) updated.', {
          entityType,
          count: updatedCount || 0
        })
      );
      setSelectedUploadBatchId(initialUploadBatchId || null);
      setSelectedCohortId(null);
    }
  }, [success, updatedCount, dispatch, t, initialUploadBatchId, sendType]);

  useEffect(() => {
    if (initialUploadBatchId) {
      setSelectedUploadBatchId(initialUploadBatchId);
      setSendType('batch');
    }
  }, [initialUploadBatchId]);

  const handleOpenModal = () => {
    if (initialUploadBatchId) {
      setSelectedUploadBatchId(initialUploadBatchId);
      setSendType('batch');
    }
    setIsModalVisible(true);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    setSelectedUploadBatchId(initialUploadBatchId || null);
    setSelectedCohortId(null);
    setSendType('batch');
    dispatch({ type: UPLOAD_BATCH_SEND_BATCH_RESET });
    dispatch({ type: COHORT_SEND_COHORT_RESET });
  };

  const handleConfirm = () => {
    if (success) {
      setIsModalVisible(false);
      setSelectedUploadBatchId(initialUploadBatchId || null);
      setSelectedCohortId(null);
      setSendType('batch');
      dispatch({ type: UPLOAD_BATCH_SEND_BATCH_RESET });
      dispatch({ type: COHORT_SEND_COHORT_RESET });
    } else if (sendType === 'batch' && selectedUploadBatchId) {
      dispatch(sendBatch(selectedUploadBatchId));
    } else if (sendType === 'cohort' && selectedCohortId) {
      dispatch(sendCohort(selectedCohortId));
    } else {
      const entityType = sendType === 'batch' ? t('upload batch') : t('cohort');
      message.warning(t('Please select an {{entityType}}', { entityType }));
    }
  };

  const handleUploadBatchChange = (uploadBatchId) => {
    setSelectedUploadBatchId(uploadBatchId);
  };

  const handleCohortChange = (cohortId) => {
    setSelectedCohortId(cohortId);
  };

  const handleSendTypeChange = (e) => {
    setSendType(e.target.value);
    setSelectedUploadBatchId(initialUploadBatchId || null);
    setSelectedCohortId(null);
    dispatch({ type: UPLOAD_BATCH_SEND_BATCH_RESET });
    dispatch({ type: COHORT_SEND_COHORT_RESET });
  };

  return (
    <>
      <Button
        type="primary"
        icon={<SendOutlined />}
        onClick={handleOpenModal}
        {...buttonProps}
      >
        {t('Send Batch')}
      </Button>

      <Modal
        title={t('Send for Post-Processing')}
        open={isModalVisible}
        onCancel={handleCancel}
        onOk={handleConfirm}
        okText={success ? t('Close') : t('Confirm and Send')}
        cancelText={t('Cancel')}
        okButtonProps={{ 
          loading: loading && !success,
          disabled: ((sendType === 'batch' && !selectedUploadBatchId) || (sendType === 'cohort' && !selectedCohortId)) && !success
        }}
        width={600}
        closable={!loading || success}
        maskClosable={!loading && !success}
      >
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          {error && (
            <Alert
              message={t('Error')}
              description={Array.isArray(error) ? error.join(', ') : error}
              type="error"
              showIcon
              closable
              onClose={() => {
                dispatch({ type: UPLOAD_BATCH_SEND_BATCH_RESET });
                dispatch({ type: COHORT_SEND_COHORT_RESET });
              }}
            />
          )}

          {success && (
            <Alert
              message={t('Success')}
              description={t('{{entityType}} sent successfully. {{count}} subject(s) updated.', {
                entityType: sendType === 'batch' ? t('Batch') : t('Cohort'),
                count: updatedCount || 0
              })}
              type="success"
              showIcon
            />
          )}

          {!success && (
            <>
              <Paragraph>
                <Text strong>
                  {t('You are about to agree that the validated data will be sent for post-processing.')}
                </Text>
              </Paragraph>
              
              <Paragraph type="secondary">
                {t('All subjects with ENROLLED status in the selected {{entityType}} will be updated to SENT status and sent for post-processing.', {
                  entityType: sendType === 'batch' ? t('batch') : t('cohort')
                })}
              </Paragraph>

              <div>
                <Text strong style={{ display: 'block', marginBottom: 8 }}>
                  {t('Send By')} <Text type="danger">*</Text>
                </Text>
                <Radio.Group value={sendType} onChange={handleSendTypeChange} style={{ marginBottom: 16 }}>
                  <Radio value="batch">{t('Batch')}</Radio>
                  <Radio value="cohort">{t('Cohort')}</Radio>
                </Radio.Group>
              </div>

              {sendType === 'batch' && (
                <div>
                  <Text strong style={{ display: 'block', marginBottom: 8 }}>
                    {t('Upload Batch')} <Text type="danger">*</Text>
                  </Text>
                  <SingleEntitySelectMiniature
                    singularEntity="uploadBatch"
                    pluralEntity="uploadBatches"
                    constantPrefix="UPLOAD_BATCH"
                    listEntities={listUploadBatches}
                    listEntitiesByIds={listUploadBatchesByIds}
                    value={selectedUploadBatchId}
                    onChange={handleUploadBatchChange}
                    primarySearchProp="fileName"
                    secondarySearchProp="fileFormat"
                    placeholder={t('Select an upload batch')}
                    isMultiSelect={false}
                    enableCreate={false}
                    enableEditing={false}
                  />
                </div>
              )}

              {sendType === 'cohort' && (
                <div>
                  <Text strong style={{ display: 'block', marginBottom: 8 }}>
                    {t('Cohort')} <Text type="danger">*</Text>
                  </Text>
                  <SingleEntitySelectMiniature
                    singularEntity="cohort"
                    pluralEntity="cohorts"
                    constantPrefix="COHORT"
                    listEntities={listCohorts}
                    listEntitiesByIds={listCohortsByIds}
                    value={selectedCohortId}
                    onChange={handleCohortChange}
                    primarySearchProp="name"
                    secondarySearchProp="status"
                    placeholder={t('Select a cohort')}
                    isMultiSelect={false}
                    enableCreate={false}
                    enableEditing={false}
                  />
                </div>
              )}
            </>
          )}
        </Space>
      </Modal>
    </>
  );
};

export default SendBatchButton;

